import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCustomer } from '../../../../hooks/useCustomer';
import { useReceivableTable } from '../../table/hooks/useReceivableTable';

import SectionInfoHeader from '../../../../components/common/SectionInfoHeader';
import SvgIcon from '../../../../components/SvgIcon/SvgIcon';
import QuickAddCustomer from '../../../../components/QuickAdd/Customer/QuickAddCustomer';
import SimpleSearchSelect from '../../../../components/Forms/Input/SimpleSearchSelect';

import SearchSelectionCard from './SearchSelectionCard/SearchSelectionCard';

const CustomerSelector = () => {
  const {
    handlerGetCustomersForDropdown,
    customersDropdown,
    saveCustomer,
  } = useCustomer();

  const {
    fetchPendingReceivablesByCustomer,
    pendingCustomerReceivables,
  } = useReceivableTable();

  const navigate = useNavigate();

  const [selectedCustomerId, setSelectedCustomerId] =
    useState<number | null>(null);

  const [selectedReceivableId, setSelectedReceivableId] =
    useState<number | null>(null);

  /**
   * 🔥 cargar clientes al iniciar
   */
  useEffect(() => {
    handlerGetCustomersForDropdown();
  }, []);

  /**
   * 🔥 cuando cambia cliente:
   * obtener cuentas pendientes desde backend
   */
  useEffect(() => {
    if (!selectedCustomerId) return;

    fetchPendingReceivablesByCustomer(
      selectedCustomerId
    );
  }, [selectedCustomerId]);

  /**
   * 🔥 selección final:
   * navegar al flujo contextual de pago
   */
  const handleReceivableSelect = (item: {
    id: number;
    number: string;
    issueDate: string;
    dueDate: string;
    totalAmount: number;
    paidAmount: number;
    balance: number;
    status: string;
    daysOverdue: number;
  }) => {
    setSelectedReceivableId(item.id);

    navigate(
      `/finance/cxc/register-payment/${item.id}`
    );
  };

  /**
   * 🔥 búsqueda dinámica backend
   */
  const handleSearchReceivables = async (
    search: string
  ) => {
    if (!selectedCustomerId) return;

    await fetchPendingReceivablesByCustomer(
      selectedCustomerId,
      search
    );
  };

  return (
    <div className="px-6 py-6">
      <SectionInfoHeader
        icon={<SvgIcon name="user" />}
        title="Cobrar a Clientes"
        description="Busca y selecciona un cliente para registrar el cobro."
        color="bg-success"
      />

      {/* 🔥 Quick Add */}
      <div className="mb-4">
        <QuickAddCustomer
          onReload={handlerGetCustomersForDropdown}
          onCustomerCreated={saveCustomer}
        />
      </div>

      {/* 🔥 Cliente */}
      <div className="mb-5 max-w-xl">
        <SimpleSearchSelect
          label="Cliente"
          name="customer"
          iconKey="user"
          selectedValue={selectedCustomerId}
          onSelect={(option) => {
            setSelectedCustomerId(option.id);
            setSelectedReceivableId(null);
          }}
          options={customersDropdown}
          required
        />
      </div>

      {/* 🔥 Cuentas pendientes */}
      <SearchSelectionCard
        title="Seleccionar cuenta por cobrar"
        description="Busca una cuenta pendiente para registrar el pago"
        label="Buscar cuenta"
        name="receivable"
        iconKey="invoice"
        options={pendingCustomerReceivables}
        selectedValue={selectedReceivableId}
        onSelect={handleReceivableSelect}
        onSearch={handleSearchReceivables}
        listTitle="Cuentas disponibles"
        emptyMessage="No hay cuentas por cobrar disponibles"
        disabled={!selectedCustomerId}
        required
      />
    </div>
  );
};

export default CustomerSelector;